<template>
  <div class="relative">
    <div
      ref="slide"
      class="overflow-x-auto overflow-y-hidden"
      role="region"
      aria-label="Slider"
      tabindex="0"
    >
      <div
        role="list"
        class="flex"
      >
        <slot />
      </div>
    </div>
    <!-- Right Arrow - Visible when active -->
    <button
      v-if="showRightArrow"
      type="button"
      :class="`absolute right-0 top-[35%] ${buttonBgColor} px-2 py-2 shadow-sm z-[1] hover:scale-105 active:scale-95 transition-transform duration-200`"
      aria-label="Next slide"
      @click="scrollBy(1)"
    >
      <v-icon-chevron-r
        :class="`h-6 w-6 ${iconColor}`"
        aria-hidden="true"
      />
    </button>

    <!-- Right Arrow - Invisible but present when at end (prevents miss-clicks) -->
    <button
      v-else-if="isOverflowed && isAtEnd"
      type="button"
      class="absolute right-0 top-[35%] px-2 py-2 z-[1] opacity-0 cursor-default"
      aria-label="End of carousel"
      @click.prevent
    >
      <v-icon-chevron-r
        class="h-6 w-6"
        aria-hidden="true"
      />
    </button>

    <!-- Left Arrow - Visible when active -->
    <button
      v-if="showLeftArrow"
      type="button"
      :class="`absolute left-0 top-[35%] ${buttonBgColor} px-2 py-2 shadow-sm z-[1] hover:scale-105 active:scale-95 transition-transform duration-200`"
      aria-label="Previous slide"
      @click="scrollBy(-1)"
    >
      <v-icon-chevron-l
        :class="`h-6 w-6 ${iconColor}`"
        aria-hidden="true"
      />
    </button>

    <!-- Left Arrow - Invisible but present when at start (prevents miss-clicks) -->
    <button
      v-else-if="isOverflowed && isAtStart"
      type="button"
      class="absolute left-0 top-[35%] px-2 py-2 z-[1] opacity-0 cursor-default"
      aria-label="Start of carousel"
      @click.prevent
    >
      <v-icon-chevron-l
        class="h-6 w-6"
        aria-hidden="true"
      />
    </button>
  </div>
</template>
<script setup>
import {
  ref, computed, onMounted, onUnmounted,
} from 'vue';

defineProps({
  buttonBgColor: {
    type: String,
    default: 'bg-gray-950',
  },
  iconColor: {
    type: String,
    default: 'text-gray-50',
  },
});

const slide = ref(null);
const scrollPosition = ref(0);

const scrollBy = (direction) => {
  // Calculate scroll amount based on viewport width for better responsive behavior
  const viewportWidth = window.innerWidth;
  let scrollAmount;

  if (viewportWidth >= 1280) { // xl
    scrollAmount = 320; // w-80 = 320px
  } else if (viewportWidth >= 1024) { // lg
    scrollAmount = 288; // w-72 = 288px
  } else if (viewportWidth >= 640) { // sm
    scrollAmount = 320; // w-80 = 320px
  } else { // < sm
    scrollAmount = 288; // w-72 = 288px
  }

  // Add gap spacing (space-x-4 = 16px)
  scrollAmount += 16;

  const finalAmount = direction > 0 ? scrollAmount : -scrollAmount;
  slide.value.scrollBy({ left: finalAmount, behavior: 'smooth' });
};

const updateScrollPosition = () => {
  if (slide.value) {
    scrollPosition.value = slide.value.scrollLeft;
  }
};

const isOverflowed = computed(() => (slide.value === null ? false : slide.value.clientWidth < slide.value.scrollWidth));
const isAtStart = computed(() => scrollPosition.value <= 0);
const isAtEnd = computed(() => {
  if (!slide.value) return false;
  return scrollPosition.value >= slide.value.scrollWidth - slide.value.clientWidth - 1; // -1 for rounding
});
const showLeftArrow = computed(() => isOverflowed.value && !isAtStart.value);
const showRightArrow = computed(() => isOverflowed.value && !isAtEnd.value);

let scrollListener;

onMounted(() => {
  if (slide.value) {
    slide.value.addEventListener('wheel', (e) => slide.value.scrollLeft += e.deltaX);
    scrollListener = () => updateScrollPosition();
    slide.value.addEventListener('scroll', scrollListener);
    // Initial position update
    updateScrollPosition();
  }
});

onUnmounted(() => {
  if (slide.value && scrollListener) {
    slide.value.removeEventListener('scroll', scrollListener);
  }
});

</script>

<style scoped>
.slider-scroll {
  /* Smooth scrolling behavior */
  scroll-behavior: smooth;

  /* Enable momentum scrolling on iOS */
  -webkit-overflow-scrolling: touch;

  /* Prevent overscroll effects */
  overscroll-behavior-x: contain;
  overscroll-behavior-y: none;

  /* Improve touch performance */
  touch-action: pan-x;

  /* Custom scrollbar for Firefox */
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent; /* Gray-300 thumb, transparent track */
}

/* Custom scrollbar for Webkit browsers (Chrome, Safari, Edge) */
.slider-scroll::-webkit-scrollbar {
  height: 6px; /* Thin scrollbar */
}

.slider-scroll::-webkit-scrollbar-track {
  background: transparent; /* Invisible track */
}

.slider-scroll::-webkit-scrollbar-thumb {
  background-color: #d1d5db; /* Gray-300 */
  border-radius: 3px;
  border: none;
}

/* Remove hover effects */
.slider-scroll::-webkit-scrollbar-thumb:hover {
  background-color: #d1d5db; /* Same color, no change on hover */
}
</style>
